/**
 * One chart's drawings, and the tool being used on it.
 *
 * Extracted for the same reason as `chartData`: these were four `useState`
 * calls in the screen component, so the app could hold one chart's worth of
 * them. A workspace with two charts on two symbols needs two sets — and the
 * tool and the selection are per-chart too, because a trendline being drawn on
 * the ES pane must not put the NQ pane into trendline mode.
 *
 * Nothing here is new behaviour. The optimism, the one error worth surfacing,
 * and the reset on a symbol change are all as they were in `App.tsx`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Drawing, DrawingKind } from '@aegis/core';
import { api, ApiError, type DrawingDto } from './api.js';
import type { Tool } from '../components/DrawingLayer.js';

/** The wire shape into the core model. Anchors are already price/time. */
function toDrawing(d: DrawingDto): Drawing {
  return {
    id: d.id, kind: d.kind as DrawingKind, symbol: d.symbol,
    anchors: d.anchors, text: d.text, colorIndex: d.colorIndex,
    locked: d.locked, createdAt: Date.parse(d.createdAt),
  };
}

export interface DrawingsOptions {
  /**
   * The plan's cap was hit. Read through a ref, so a caller may pass a fresh
   * closure every render without causing a refetch.
   */
  readonly onError?: (message: string) => void;
}

export interface DrawingsState {
  readonly drawings: Drawing[];
  /** How many this plan allows, or null when it has not been said. */
  readonly limit: number | null;
  readonly tool: Tool;
  readonly setTool: (t: Tool) => void;
  readonly selectedId: string | null;
  readonly select: (id: string | null) => void;
  readonly commit: (d: Drawing) => void;
  /** Delete the selected drawing. Does nothing when there is none. */
  readonly remove: () => Promise<void>;
  readonly clear: () => Promise<void>;
}

export function useDrawings(symbol: string, options: DrawingsOptions = {}): DrawingsState {
  const [drawings, setDrawings] = useState<Drawing[]>([]);
  const [limit, setLimit] = useState<number | null>(null);
  const [tool, setTool] = useState<Tool>('cursor');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const onError = useRef(options.onError);
  useEffect(() => { onError.current = options.onError; });

  useEffect(() => {
    // Empty for the render between mount and whatever resolves a default.
    if (!symbol) return undefined;

    let cancelled = false;
    // A tool and a selection belong to the symbol they were chosen on. Carrying
    // either across would leave the pane armed to draw on an instrument the
    // trader has not looked at yet.
    setSelectedId(null);
    setTool('cursor');
    /*
      And the drawings themselves, which the version in `App.tsx` did not do.

      `DrawingLayer` renders whatever list it is handed — it does not filter by
      symbol — so between a symbol change and the new fetch landing, the old
      instrument's trendlines were drawn over the new one's chart. That is the
      same failure the chart's own blanking exists to prevent, and with two
      panes on two symbols it stops being a flicker and becomes a chart that is
      routinely wrong about which lines belong to it.
    */
    setDrawings([]);
    void api.drawings(symbol)
      .then((res) => {
        if (cancelled) return;
        setDrawings(res.drawings.map(toDrawing));
        setLimit(res.limit);
      })
      // Swallowed, not emptied. Clearing here was load-bearing before the list
      // was cleared up front; now the only state it could reach is a drawing
      // made between the symbol change and the failure, and taking that away
      // is the opposite of what the trader asked for.
      .catch(() => {});
    return () => { cancelled = true; };
  }, [symbol]);

  /**
   * Optimistic, then persisted.
   *
   * A drag fires this on every pointer move, so waiting for the server would
   * make the line trail the cursor by a round trip. Local state is the truth
   * during the gesture; the save is fire-and-forget and the next symbol change
   * reconciles.
   */
  const commit = useCallback((d: Drawing) => {
    setDrawings((prev) => {
      const idx = prev.findIndex((x) => x.id === d.id);
      return idx === -1 ? [...prev, d] : prev.map((x) => (x.id === d.id ? d : x));
    });
    void api.saveDrawing({
      id: d.id, symbol: d.symbol, kind: d.kind,
      anchors: d.anchors.map((a) => ({ time: Math.round(a.time), price: a.price })),
      text: d.text ?? '', colorIndex: d.colorIndex ?? 0, locked: d.locked ?? false,
    }).catch((e) => {
      // The one failure worth surfacing: the plan's cap. Anything else is a
      // transient the next save will fix. The optimistic drawing is taken back
      // rather than left on a chart it was never stored on.
      if (e instanceof ApiError && e.status === 402) {
        onError.current?.(e.message);
        setDrawings((prev) => prev.filter((x) => x.id !== d.id));
      }
    });
  }, []);

  const remove = useCallback(async () => {
    if (!selectedId) return;
    const id = selectedId;
    setDrawings((prev) => prev.filter((d) => d.id !== id));
    setSelectedId(null);
    await api.deleteDrawing(id).catch(() => {});
  }, [selectedId]);

  const clear = useCallback(async () => {
    setDrawings([]);
    setSelectedId(null);
    await api.clearDrawings(symbol).catch(() => {});
  }, [symbol]);

  return {
    drawings, limit, tool, setTool, selectedId, select: setSelectedId,
    commit, remove, clear,
  };
}
